import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ShieldCheck, Pencil } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEBICHECK_CONFIG = {
  none: { label: 'Not Submitted', class: 'bg-slate-100 text-slate-500 border-slate-200' },
  submitted: { label: 'Submitted', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  active: { label: 'Active', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', class: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', class: 'bg-gray-200 text-gray-600 border-gray-300' },
};

export default function DebiCheckTag({ app, getAuthHeader, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ status: 'none', mandate_reference: '' });
  const [saving, setSaving] = useState(false);

  const status = app.debicheck_status || 'none';
  const cfg = DEBICHECK_CONFIG[status] || DEBICHECK_CONFIG.none;

  const openDialog = () => {
    setForm({ status, mandate_reference: app.debicheck_mandate_reference || '' });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/customer-applications/${app.id}/debicheck`,
        { debicheck_status: form.status, mandate_reference: form.mandate_reference }, getAuthHeader());
      toast.success('DebiCheck status updated');
      setOpen(false);
      onUpdated && onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update DebiCheck status'); }
    setSaving(false);
  };

  return (
    <>
      <button
        onClick={openDialog}
        className={`inline-flex flex-col items-start gap-0.5 rounded-md border px-1.5 py-0.5 hover:opacity-80 transition-opacity cursor-pointer ${cfg.class}`}
        data-testid={`debicheck-tag-${app.id}`}
        title="Update DebiCheck status"
      >
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold">
          <ShieldCheck className="w-3 h-3" />{cfg.label}<Pencil className="w-2.5 h-2.5 opacity-50" />
        </span>
        {app.debicheck_mandate_reference && (
          <span className="font-mono text-[9px] opacity-80" title={app.debicheck_mandate_reference}>
            {app.debicheck_mandate_reference.length > 10 ? `…${app.debicheck_mandate_reference.slice(-8)}` : app.debicheck_mandate_reference}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="debicheck-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" />DebiCheck — Loan {app.application_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-md px-3 py-2 text-xs text-slate-600">
              <p><span className="font-semibold">Loan reference:</span> <span className="font-mono">{app.application_number}</span></p>
              <p><span className="font-semibold">Customer:</span> {app.full_name}</p>
              {app.debicheck_by && (
                <p className="text-slate-400 mt-1">Last updated by {app.debicheck_by}{app.debicheck_updated_at ? ` on ${new Date(app.debicheck_updated_at).toLocaleString()}` : ''}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>DebiCheck Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="debicheck-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DEBICHECK_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`debicheck-status-${k}`}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>DebiCheck Mandate Reference</Label>
              <Input value={form.mandate_reference} onChange={e => setForm(f => ({ ...f, mandate_reference: e.target.value }))}
                placeholder="e.g. MND-000123456" data-testid="debicheck-mandate-input" />
              <p className="text-[11px] text-slate-400">The mandate reference returned by the bank when the DebiCheck was registered</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="debicheck-save-btn">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
